import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import type { Ref } from 'vue';
import type { SubscriptionSettingsCreateViewModel, SubscriptionSettingsViewModel, SubscriptionCostViewModel } from '@cognitoforms/api/services/subscription-service';
import { getSubscriptionSettings, updateSubscriptionSettings, generateSubscriptionCost } from '@cognitoforms/api/services/subscription-service';
import type { ValidationObserver } from 'vee-validate';
import type SubscriptionBilling from '../../../../components/app/SubscriptionBilling.vue';
import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
import session from '@cognitoforms/api/services/session';
import type VueRouter from 'vue-router';
import type { Route } from 'vue-router';

export interface AnnualCommitments {
	isAnnual: boolean;
	users: number | null;
	wallet: number | null;
}

export interface UseCheckoutBillingOptions {
	plan: string | undefined;
	emit: (event: 'loading' | 'close' | 'handle-close' | 'change-plan-status' | 'component-loaded' | 'cost-updated' | 'cost-loading' | 'cost-throttled', ...args: unknown[]) => void;
	// When provided, the form runs the embedded-checkout behaviour: it folds the annual
	// commitments into the cost request, refetches when they change, and emits cost updates.
	// A null value is a monthly plan with no annual commitment.
	annualCommitments?: Ref<AnnualCommitments | null>;
	// When provided, replaces the default "update subscription then navigate" submit step
	// once billing info is saved. The wallet refill dialog uses this to charge the top-up
	// against the saved card and stay in place instead of routing to a confirmation page.
	onSubmit?: (model: SubscriptionSettingsViewModel) => Promise<void>;
}

export function useCheckoutBilling(options: UseCheckoutBillingOptions) {
	const proxy = getCurrentInstance()!.proxy as unknown as { $route: Route; $router: VueRouter };
	const emit = options.emit;
	const annualCommitments = options.annualCommitments;
	const tracksAnnualCommitments = !!annualCommitments;

	const observer = ref<InstanceType<typeof ValidationObserver> | null>(null);
	const subscriptionBilling = ref<InstanceType<typeof SubscriptionBilling> | null>(null);

	const viewModel = ref<SubscriptionSettingsViewModel>({} as SubscriptionSettingsViewModel);
	const costModel = ref<SubscriptionCostViewModel>({} as SubscriptionCostViewModel);
	const publishableKey = ref('');
	const cardErrorMessage = ref('');
	const loading = ref(true);
	const changePlanButtonStatus = ref<ButtonStatus>('default');
	const subscriptionCostLoading = ref(false);
	const subscriptionCostThrottled = ref(false);
	let costController: AbortController | null = null;
	let viewModelReady = false;

	const isCustomerAddressValidationEnabled = computed(() => session.value?.enabledFeatures?.CustomerAddressValidation);

	watch(changePlanButtonStatus, val => emit('change-plan-status', val), { immediate: true });
	watch(loading, val => emit('loading', val), { immediate: true });

	if (tracksAnnualCommitments) {
		watch(subscriptionCostLoading, val => emit('cost-loading', val));
		watch(subscriptionCostThrottled, val => emit('cost-throttled', val));
	}

	function withAnnualCommitments(model: SubscriptionSettingsViewModel): SubscriptionSettingsViewModel {
		if (!annualCommitments)
			return model;
		const commitments = annualCommitments.value;
		if (commitments === null)
			return model;
		return {
			...model,
			IsAnnual: commitments.isAnnual,
			PrepaymentUsers: commitments?.users ?? undefined,
			RequestedUsageWallet: commitments?.wallet ?? undefined
		};
	}

	function getObserver() {
		return observer.value as InstanceType<typeof ValidationObserver>;
	}

	async function updatePlan(shouldValidateAddress = true) {
		const isVeeValidateValid = await getObserver().validate();
		const billing = subscriptionBilling.value as InstanceType<typeof SubscriptionBilling>;
		const isCardValid = billing.validateBillingFields();

		if (!isVeeValidateValid || !isCardValid)
			return;

		cardErrorMessage.value = '';
		changePlanButtonStatus.value = 'validating';
		try {
			await billing.handleSubmit(shouldValidateAddress);
		}
		catch (e) {
			changePlanButtonStatus.value = 'error';
		}
	}

	function submit() {
		if (loading.value)
			return;
		return updatePlan(isCustomerAddressValidationEnabled.value);
	}

	async function updateInfo(updatedViewModel: SubscriptionSettingsViewModel) {
		try {
			if (options.onSubmit) {
				await options.onSubmit(withAnnualCommitments(updatedViewModel));
				changePlanButtonStatus.value = 'success';
				return;
			}
			const result = await updateSubscriptionSettings(withAnnualCommitments(updatedViewModel));
			proxy.$router.replace(result);
			changePlanButtonStatus.value = 'success';
		}
		catch (error) {
			cardErrorMessage.value = error?.message ?? 'Unable to generate invoice';
			changePlanButtonStatus.value = 'error';
		}
	}

	async function updateSubscriptionCost(updatedViewModel?: SubscriptionSettingsViewModel) {
		if (updatedViewModel)
			viewModel.value = updatedViewModel;
		costController?.abort();
		const controller = costController = new AbortController();
		subscriptionCostLoading.value = true;
		try {
			const cost = await generateSubscriptionCost(withAnnualCommitments(viewModel.value), { signal: controller.signal });
			if (controller.signal.aborted)
				return;
			costModel.value = cost;
			// Pin the proration anchor to what was used during estimation
			viewModel.value.ProrationDate = new Date(cost.prorationDate);
			viewModel.value.ProrationToken = cost.prorationToken;
			subscriptionCostThrottled.value = false;
			if (tracksAnnualCommitments)
				emit('cost-updated', costModel.value);
		}
		catch {
			if (controller.signal.aborted)
				return;
			subscriptionCostThrottled.value = true;
		}
		finally {
			if (!controller.signal.aborted)
				subscriptionCostLoading.value = false;
		}
	}

	function setErrors(errors: Record<string, string[]> | null) {
		changePlanButtonStatus.value = 'error';
		if (errors)
			getObserver().setErrors(errors);
	}

	if (annualCommitments) {
		watch(annualCommitments, () => {
			if (viewModelReady)
				updateSubscriptionCost();
		});
	}

	onMounted(async () => {
		const query = proxy.$route.query;
		const createViewModel: SubscriptionSettingsCreateViewModel = {
			Plan: (options.plan ?? query.plan ?? query.plancode).toString(),
			PrepayUsers: Number(query.prepayusers),
			PrepayGuests: Number(query.prepayguests),
			PrepayMonths: Number(query.prepaymonths),
			PrepayTemplates: Number(query.prepaytemplates),
			Source: query.source?.toString()
		};
		let initialCostController: AbortController | null = null;
		try {
			viewModel.value = await getSubscriptionSettings(createViewModel);
			publishableKey.value = viewModel.value.PublishableKey;
			viewModelReady = true;
			initialCostController = costController = new AbortController();
			const cost = await generateSubscriptionCost(withAnnualCommitments(viewModel.value), { signal: initialCostController.signal });
			if (!initialCostController.signal.aborted) {
				costModel.value = cost;
				viewModel.value.ProrationDate = new Date(cost.prorationDate);
				viewModel.value.ProrationToken = cost.prorationToken;
				if (tracksAnnualCommitments)
					emit('cost-updated', costModel.value);
			}
		}
		catch {
			if (!initialCostController?.signal.aborted) {
				emit('handle-close');
				return;
			}
		}

		loading.value = false;
	});

	return {
		observer,
		subscriptionBilling,
		viewModel,
		costModel,
		publishableKey,
		cardErrorMessage,
		loading,
		changePlanButtonStatus,
		subscriptionCostLoading,
		subscriptionCostThrottled,
		isCustomerAddressValidationEnabled,
		submit,
		updatePlan,
		updateInfo,
		updateSubscriptionCost,
		setErrors,
		getObserver
	};
}
